import { Text } from '@react-three/drei'
import { useAppStore } from '@store'
import { SceneObjects, Walls } from '@types'
import { calcRotation } from '@utils/generalMaths/calcRotation'
import is2DMode from '@utils/is2DMode'
import isHole from '@utils/sceneObjects/assertions/isHole'
import getId from '@utils/sceneObjects/getId'
import { getInnerAngle } from '@utils/sceneObjects/walls/getInnerAngle'
import VerticalLine from './VerticalLine'
type Props = { id: string; offset: number; elements: SceneObjects[] }
export default function HoleDimentions({ id, offset }: Props) {
  const item = useAppStore((store) => store.items.get(id)) as Walls
  const items = useAppStore.use.items()
  const width = item.width
  const start = item.start
  const end = item.end
  const elements = item.holes
  const rotation = item.rotation
  const unit = useAppStore((store) => store.prefs?.UNIDADMEDIDA)

  const pref = useAppStore.use.prefs()?.['VERUNIDADESENCOTAS']
  const PREFS_COLOR_COTA = useAppStore((store) => store.prefs?.['C/LINEASCOTA'])
  const HORIZONTAL_WALL_ARGS = [width, 7] satisfies [
    width?: number | undefined,
    height?: number | undefined,
    depth?: number | undefined,
    widthSegments?: number | undefined,
    heightSegments?: number | undefined,
    depthSegments?: number | undefined,
  ]
  const prevWall = item.prevWall
  const prevWallObject = useAppStore.use.items().get(prevWall) as Walls
  const { cp } = getInnerAngle(start, end, prevWallObject?.start, prevWallObject?.end)
  const thickness = item.thickness
  const PREFS_COLOR_TEXTO = useAppStore((store) => store.prefs?.['C/COTA'])
  const FONT_SIZE = useAppStore.use.prefs()?.['T/LETRACOTA'] ?? 100
  const neededRotation = calcRotation(cp, rotation)
  const mode = useAppStore.use.mode()
  const is2dmode = is2DMode(mode)
  const offsets = is2dmode ? -thickness - offset : thickness + offset + 70

  const elementArray = Array.from(elements)
    .map((el) => items.get(el))
    .sort((a, b) => a.distI - b.distI)
    .flatMap((el, i, arr) => {
      if (!isHole(el)) return
      if (i === 0 && arr.length !== 1) {
        return [
          {
            distI: 0,
            distD: el?.distI + el?.distD,
            width: el?.distI,
            id: getId('holes'),
            type: 'holes',
          },
          el,
        ]
      }
      if (i === 0 && arr.length === 1) {
        return [
          {
            distI: 0,
            distD: el?.distI + el?.distD,
            width: el?.distI,
            id: getId('holes'),
            type: 'holes',
          },
          el,
          {
            distI: el?.distI + el?.width,
            distD: width,
            width: width - el.distI - el.width,
            type: 'holes',
            id: getId('holes'),
          },
        ]
      }
      if (i === arr.length - 1) {
        const prev = arr[i - 1]
        return [
          {
            type: 'holes',
            distI: prev?.distI + prev.width,
            distD: el.distI,
            width: el.distI - (prev?.distI + prev?.width),
            id: getId('holes'),
          },
          el,
          {
            type: 'holes',
            distI: el?.distI + el?.width,
            distD: 0,
            width: width - el?.distI - el?.width,
            id: getId('holes'),
          },
        ]
      }
      const prev = arr[i - 1]

      return [
        {
          type: 'holes',
          distI: prev?.distI + prev.width,
          distD: el.distI,
          width: el.distI - (prev?.distI + prev?.width),
          id: getId('holes'),
        },
        el,
      ]
    })
  // const verticalX = -width / 2 + el.distI
  return (
    <group>
      {elementArray.map((el) => {
        if (!isHole(el)) return null
        const verticalX = -width / 2 + el.distI

        return (
          <group
            key={`inside-hole-${el.distI}-${el.distD}-${el.id}}-${width}-${start.join(
              '+',
            )}-${end.join('¨')}`}
          >
            <VerticalLine position={[verticalX, 2, offsets]} />
            <VerticalLine position={[verticalX + el.width, 2, offsets]} />
            <mesh
              position={[0, 2, offsets]}
              rotation={[-Math.PI / 2, 0, 0]}
            >
              <boxGeometry args={HORIZONTAL_WALL_ARGS} />
              <meshBasicMaterial color={PREFS_COLOR_COTA} />
              {el.width && el.width > 1 && (
                <Text
                  position={[verticalX + el.width / 2, 75, 0]}
                  fontSize={FONT_SIZE}
                  characters='abcdefghijklmnopqrstuvwxyz0123456789!ºª'
                  color={PREFS_COLOR_TEXTO}
                  rotation={[0, ...neededRotation]}
                >
                  {`${Math.trunc(el.width)} ${pref ? unit : ''}`}
                </Text>
              )}
            </mesh>
          </group>
        )
      })}
    </group>
  )
}
